

import { useState, useEffect } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import { toUnit, unitSuffix } from "@/lib/pricing";

interface Service {
  id: number;
  name: string;
  selling_price: number;
  base_price?: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
  unit?: string;
  category?: string | null;
}

interface Vendor {
  id: number;
  name: string;
}

interface QuotationItem {
  id?: number;
  service_id?: number;
  description?: string;
  vendor_id?: number;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  notes?: string;
}

interface AddServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (service: QuotationItem) => void;
  services: Service[];
  vendors: Vendor[];
  initialData?: QuotationItem;
  /** Guest count of the quotation — a per-guest service defaults its quantity to it */
  guests?: number;
}

/** "Catering · Veg Buffet - ₹450 / guest" */
const serviceLabel = (s: Service) => {
  const suffix = unitSuffix(s.unit);
  const price = Math.round(Number(s.selling_price) || 0).toLocaleString("en-IN");
  return `${s.category ? `${s.category} · ` : ""}${s.name} - ₹${price}${suffix ? ` ${suffix}` : ""}`;
};

const byCategory = (a: Service, b: Service) =>
  (a.category || "~").localeCompare(b.category || "~") || a.name.localeCompare(b.name);

const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

export default function AddServiceModal({
  isOpen,
  onClose,
  onAdd,
  services,
  vendors,
  initialData,
  guests = 0,
}: AddServiceModalProps) {
  const [formData, setFormData] = useState<QuotationItem>({
    service_id: undefined,
    description: "",
    vendor_id: undefined,
    quantity: 1,
    unit_price: 0,
    cost_price: 0,
    notes: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof QuotationItem, string>>>({});

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          id: initialData.id,
          service_id: initialData.service_id,
          description: initialData.description || "",
          vendor_id: initialData.vendor_id,
          quantity: initialData.quantity || 1,
          unit_price: initialData.unit_price || 0,
          cost_price: initialData.cost_price || 0,
          notes: initialData.notes || "",
        });
      } else {
        setFormData({
          service_id: undefined,
          description: "",
          vendor_id: undefined,
          quantity: 1,
          unit_price: 0,
          cost_price: 0,
          notes: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  const handleChange = (field: keyof QuotationItem, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleServiceSelect = (serviceId: string) => {
    const service = services.find(s => String(s.id) === serviceId);
    if (service) {
      setFormData(prev => ({
        ...prev,
        service_id: service.id,
        description: service.name,
        unit_price: service.selling_price || 0,
        cost_price: service.base_price || 0,
        vendor_id: service.vendor_id || undefined,
        // Per-guest services are charged for every guest
        quantity: toUnit(service.unit) === "guest" && guests > 0 ? guests : prev.quantity,
      }));
    }
  };

  const selectedUnit = services.find(s => s.id === formData.service_id)?.unit;


  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof QuotationItem, string>> = {};

    if (!formData.service_id) newErrors.service_id = "Service is required";
    if (!formData.description?.trim()) newErrors.description = "Description is required";
    if (formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
    if (formData.unit_price < 0) newErrors.unit_price = "Price cannot be negative";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onAdd(formData);
    }
  };

  const lineTotal = formData.quantity * formData.unit_price;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-amber-50 to-white">
          <h3 className="text-lg font-bold text-stone-800">
            {initialData ? "Edit Service" : "Add Service"}
          </h3>
          <p className="text-xs text-stone-500 mt-1">Select service - vendor auto-assigned. Customize quantity & price</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Service Selection */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Service *</label>
            <SearchableDropdown
              options={[...services].sort(byCategory).map(s => ({
                value: String(s.id),
                label: serviceLabel(s),
              }))}
              value={formData.service_id ? String(formData.service_id) : ""}
              onChange={handleServiceSelect}
              placeholder="Search and select service"
            />
            {errors.service_id && <p className="text-xs text-rose-500 mt-1">{errors.service_id}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Description *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Service description"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
            {errors.description && <p className="text-xs text-rose-500 mt-1">{errors.description}</p>}
          </div>

          {/* Vendor Display - Auto-assigned from Service */}
          {formData.vendor_id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-stone-700 mb-1">Assigned Vendor</p>
              <p className="text-sm font-medium text-blue-700">
                {vendors.find(v => v.id === formData.vendor_id)?.name || 'Unknown Vendor'}
              </p>
              <button
                type="button"
                onClick={() => handleChange("vendor_id", undefined)}
                className="text-xs text-blue-600 hover:text-blue-800 mt-2 underline"
              >
                Clear vendor assignment
              </button>
            </div>
          )}

          {/* Quantity & Price */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">Quantity *</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              />
              {errors.quantity && <p className="text-xs text-rose-500 mt-1">{errors.quantity}</p>}
              {toUnit(selectedUnit) === "guest" && guests > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">Per guest · {guests} guests</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">
                Unit Price *{unitSuffix(selectedUnit) && <span className="font-normal text-stone-500"> {unitSuffix(selectedUnit)}</span>}
              </label>
              <div className="relative">
                <INRIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => handleChange("unit_price", parseFloat(e.target.value) || 0)}
                  className="w-full pl-6 pr-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                />
              </div>
              {errors.unit_price && <p className="text-xs text-rose-500 mt-1">{errors.unit_price}</p>}
            </div>
          </div>

          {/* Line Total */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-stone-600 mb-1">Line Total</p>
            <p className="text-lg font-bold text-amber-700">
              ₹{lineTotal.toLocaleString('en-IN')}
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Notes</label>
            <textarea
              value={formData.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Any special requirements..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-6 py-3 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold text-sm hover:bg-amber-700 transition-colors"
          >
            {initialData ? "Update" : "Add"} Service
          </button>
        </div>
      </div>
    </div>
  );
}
